import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, Search, ShoppingCart, User } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { Badge } from '@dfq/shared/components/ui/badge';
import { Button } from '@dfq/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dfq/shared/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@dfq/shared/components/ui/sheet';
import { cn } from '@dfq/shared/lib/utils';

/**
 * Storefront top navigation rendered on every public page.
 *
 * Figma: Storefront file `4puwgAMg81mAcbW487slM2` canonical frame `10:2`
 * / refreshed `43:3`. The Storefront silhouette is a true charcoal bar
 * with an AMBER bottom-border accent — opt in via `surface="chrome"`,
 * which resolves to `bg-storefront-chrome` (`#1a1a1a`) +
 * `border-accent` (`#ffaa00`). The default `surface` keeps the original
 * Console-flavored `bg-sidebar` slate + `border-brand-primary` indigo so
 * any non-Storefront consumer that hasn't migrated doesn't regress.
 *
 * Layout:
 * - Desktop (`md+`): logo (left) · primary nav links (center) · right
 *   cluster [Search icon button, account (DropdownMenu when signed in,
 *   otherwise a "Sign in" button), Cart icon button with Badge overlay].
 * - Mobile (`< md`): logo + hamburger ghost Button which opens a shadcn
 *   `Sheet` (`side=left`, `w=[280px]`) containing the stacked nav + account
 *   actions + cart link.
 *
 * Responsive switching is CSS-only (`hidden md:flex` / `md:hidden`) so the
 * component renders correctly in Storybook / SSR / any non-DOM environment
 * without a `matchMedia` flicker.
 *
 * Search + Sign in are callbacks only — the component owns no search logic
 * (SF-SEARCH-003 wires a typeahead onto `onSearchClick`).
 *
 * SF-INFRA-001 extensions:
 * - Nav items can now carry `children` to render as a `DropdownMenu`
 *   (e.g. the Products mega-dropdown). A top-level NavLink is still used
 *   when `children` is absent.
 * - The header transitions to a `backdrop-blur` + translucent background
 *   once the user scrolls past the top of the page (`isScrolled`).
 *   Consumers can override detection via the `isScrolled` prop when they
 *   need to force the state (stories, tests, scroll-container consumers).
 */

export interface NavBarNavLink {
  /** Visible label (e.g. "Gallery", "How It Works"). */
  label: string;
  /** Destination path rendered as a `react-router-dom` NavLink. */
  to: string;
}

export interface NavBarNavGroup {
  /** Visible label for the dropdown trigger (e.g. "Products"). */
  label: string;
  /**
   * Optional top-level destination — rendered as a Link inside the
   * dropdown (e.g. "All products") so keyboard users can still reach the
   * parent page.
   */
  to?: string;
  /** Child links rendered inside the `DropdownMenu`. */
  children: NavBarNavLink[];
}

export type NavBarItem = NavBarNavLink | NavBarNavGroup;

function isNavGroup(item: NavBarItem): item is NavBarNavGroup {
  return 'children' in item && Array.isArray(item.children);
}

export interface NavBarUser {
  /**
   * Display name shown in the account dropdown and the trigger's accessible
   * label. Optional: when blank the dropdown shows "Account" and the label
   * is just "Open account menu".
   */
  name?: string;
}

function displayName(user: NavBarUser): string {
  return user.name?.trim() || 'Account';
}

function accountMenuLabel(user: NavBarUser): string {
  const name = user.name?.trim();
  return name ? `Open account menu for ${displayName(user)}` : 'Open account menu';
}

const navBarVariants = cva(
  'sticky top-0 z-40 h-16 w-full border-b text-on-primary transition-[background-color,backdrop-filter] duration-200',
  {
    variants: {
      /**
       * Surface tokens for the bar. The default keeps the original
       * Console-flavored slate + indigo accent so any non-Storefront
       * consumer (e.g. shared layouts that haven't migrated yet) doesn't
       * regress. The `chrome` variant matches the Storefront Figma
       * (`43:3`): a true charcoal surface with an amber bottom-border
       * accent.
       */
      surface: {
        default: 'border-brand-primary bg-sidebar',
        chrome: 'border-accent bg-storefront-chrome',
      },
      /**
       * Reserved for future layout tweaks (e.g. a compact nav on embedded
       * surfaces). Default matches Figma `10:2` 64px height.
       */
      density: {
        default: '',
      },
    },
    defaultVariants: { surface: 'default', density: 'default' },
  },
);

/**
 * Inner-container wrapper — aligns the header content with the storefront
 * page-content max-width so the chrome tracks the same gutter rhythm as
 * every other route view.
 */
const NAV_BAR_INNER_CLASS =
  'mx-auto flex h-full w-full max-w-screen-xl items-center justify-between px-4 sm:px-6 lg:px-8';

/** Resolved Tailwind classes per surface variant for nested elements. */
type NavBarSurface = NonNullable<VariantProps<typeof navBarVariants>['surface']>;

interface SurfaceClasses {
  /** Translucent background for the scroll-aware "frosted" state. */
  scrolledBg: string;
  /** Active/hover background applied to right-cluster ghost buttons. */
  activeBg: string;
  /** Focus ring offset color, matched to the surface. */
  ringOffset: string;
}

const SURFACE_CLASSES: Record<NavBarSurface, SurfaceClasses> = {
  default: {
    scrolledBg: 'bg-sidebar/80 backdrop-blur-md',
    activeBg: 'hover:bg-sidebar-active hover:text-on-primary',
    ringOffset: 'focus-visible:ring-offset-sidebar',
  },
  chrome: {
    scrolledBg: 'bg-storefront-chrome/80 backdrop-blur-md',
    activeBg: 'hover:bg-storefront-chrome-divider hover:text-on-primary',
    ringOffset: 'focus-visible:ring-offset-storefront-chrome',
  },
};

export interface NavBarProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'>,
    VariantProps<typeof navBarVariants> {
  /**
   * Surface variant — `default` keeps the Console-flavored slate + indigo
   * accent, `chrome` switches to the Storefront charcoal + amber accent
   * (Figma `43:3`). Omitting falls through to the cva default (`default`).
   */
  // surface — typed by VariantProps<typeof navBarVariants>; documented here
  // for IntelliSense and to keep the prop discoverable.
  /** Primary nav items rendered in the center cluster (desktop) and the drawer (mobile). */
  navItems: NavBarItem[];
  /** Cart item count — when `> 0` a Badge is layered onto the cart icon. */
  cartCount?: number;
  /** Destination for the cart icon button. Defaults to `/cart`. */
  cartHref?: string;
  /** Called when the search icon button is activated (desktop + mobile). */
  onSearchClick?: () => void;
  /** Called when the "Sign in" button is activated (anonymous users only). */
  onSignIn?: () => void;
  /** Called when the "Sign out" menu item is activated (signed-in users). */
  onSignOut?: () => void;
  /** Called when the "Account" menu item is activated (signed-in users). */
  onAccountClick?: () => void;
  /** Optional signed-in user; when absent the right cluster shows "Sign in". */
  user?: NavBarUser;
  /** Brand wordmark rendered as the left-aligned logo link. Defaults to `"DFQ"`. */
  brand?: React.ReactNode;
  /** Destination of the brand logo link. Defaults to `"/"`. */
  brandHref?: string;
  /**
   * SF-BRAND-101 — admin-configured logo image URL. When present, renders
   * an `<img>` in place of the `brand` text wordmark inside the home link.
   * When absent/empty, the NavBar falls back to the `brand` text wordmark.
   * Sourced from the `navigation.header_logo_url` setting; the container
   * owns fetching it, not this capsule.
   */
  logoSrc?: string;
  /** Accessible alt text for `logoSrc`. Defaults to the `brand` text (or `"DFQ Printing"` when `brand` isn't a string). */
  logoAlt?: string;
  /**
   * Force the scroll-aware "frosted" state. When omitted, the component
   * detects the window scroll position itself. Stories and tests can pin
   * the state by passing an explicit boolean.
   */
  isScrolled?: boolean;
  /**
   * SH-NOTIFY-002 — slot for the live `<NotificationBell>` capsule. When
   * supplied, the parent provides the bell capsule fully wired (with
   * Redux + polling + navigation) and the NavBar renders it inside the
   * right cluster (between Account and Cart on the chrome surface;
   * before the cart icon on the default surface). Keeps the nav bar
   * presentational: it knows nothing about the notifications slice.
   */
  notificationsSlot?: React.ReactNode;
  /**
   * SF-NAV-001 — slot for custom primary nav content rendered before the
   * static `navItems` (desktop only). Used by `PublicLayout` to inject
   * the `CategoryNavMenu` dropdown as the first nav entry without
   * encoding taxonomy logic inside the NavBar.
   */
  primaryNavSlot?: React.ReactNode;
  /** SF-SEARCH-003 — slot for wired SearchBarContainer in the right cluster; replaces the plain Search button. */
  searchBarSlot?: React.ReactNode;
  /** Whether to render the search control in the right cluster. Defaults to `true`. */
  showSearch?: boolean;
}

interface RightClusterProps {
  user?: NavBarUser;
  cartCount: number;
  cartHref: string;
  onSearchClick?: () => void;
  onSignIn?: () => void;
  onSignOut?: () => void;
  onAccountClick?: () => void;
  /** Optional notification-bell slot rendered between Account and Cart. */
  notificationsSlot?: React.ReactNode;
  /** Optional wired SearchBar slot — replaces the plain "Search" button when provided. */
  searchBarSlot?: React.ReactNode;
  /** Whether to render the search control. Defaults to `true`. */
  showSearch?: boolean;
  /** Surface-derived hover bg for ghost buttons in the cluster. */
  activeBg: string;
  /** Resolved surface — chrome flips the cluster from icons to text labels. */
  surface: NavBarSurface;
}

/**
 * Shared Tailwind class string applied to each text label in the chrome
 * right cluster. Matches Figma `43:9`: Inter Regular 13px, white at
 * 70% opacity, hover fills to 100%.
 */
const chromeRightLinkClass =
  'text-[13px] font-normal leading-none text-on-primary opacity-70 transition-opacity outline-none hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-storefront-chrome';

/**
 * Desktop right cluster — search, account (dropdown or sign-in), cart with
 * badge. Extracted so the mobile drawer can reuse a similar stack.
 *
 * Two visual modes:
 * - `surface="default"` keeps the original icon-button cluster (Console +
 *   non-migrated consumers).
 * - `surface="chrome"` renders a plain text cluster matching Figma `43:9`:
 *   `Search · Account · Cart (N)` in Inter 13px at 70% opacity.
 */
function RightCluster({
  user,
  cartCount,
  cartHref,
  onSearchClick,
  onSignIn,
  onSignOut,
  onAccountClick,
  notificationsSlot,
  searchBarSlot,
  showSearch = true,
  activeBg,
  surface,
}: RightClusterProps) {
  const hasCart = cartCount > 0;
  const cartLabel = cartCount > 99 ? '99+' : String(cartCount);
  const cartAriaLabel = `Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`;

  if (surface === 'chrome') {
    // Figma 43:9 — text-label cluster. 24px gap, Inter 13px, opacity 70→100 on hover.
    return (
      <div className="flex items-center gap-[24px]">
        {showSearch ? (
          searchBarSlot !== undefined ? (
            <div data-slot="navbar-search">{searchBarSlot}</div>
          ) : (
            <button
              type="button"
              onClick={onSearchClick}
              className={chromeRightLinkClass}
            >
              Search
            </button>
          )
        ) : null}

        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={accountMenuLabel(user)}
                className={chromeRightLinkClass}
              >
                Account
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              <div className="px-2 py-1.5 text-sm font-medium text-text-primary">
                {displayName(user)}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onAccountClick}>Account</DropdownMenuItem>
              <DropdownMenuItem onSelect={onSignOut}>Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            type="button"
            onClick={onSignIn}
            className={chromeRightLinkClass}
          >
            Sign in
          </button>
        )}

        {notificationsSlot !== undefined ? (
          <div data-slot="navbar-notifications">{notificationsSlot}</div>
        ) : null}

        <Link
          to={cartHref}
          aria-label={cartAriaLabel}
          data-testid="navbar-cart-link"
          className={chromeRightLinkClass}
        >
          Cart ({cartLabel})
        </Link>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {showSearch ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Search"
          className={cn('size-11 text-on-primary', activeBg)}
          onClick={onSearchClick}
        >
          <Search aria-hidden="true" />
        </Button>
      ) : null}

      {user ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={accountMenuLabel(user)}
              className={cn('size-11 text-on-primary', activeBg)}
            >
              <User aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <div className="px-2 py-1.5 text-sm font-medium text-text-primary">
              {displayName(user)}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onAccountClick}>Account</DropdownMenuItem>
            <DropdownMenuItem onSelect={onSignOut}>Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onSignIn}
          className={cn('min-h-11 text-on-primary', activeBg)}
        >
          Sign in
        </Button>
      )}

      {notificationsSlot !== undefined ? (
        <div data-slot="navbar-notifications">{notificationsSlot}</div>
      ) : null}

      <Button
        asChild
        variant="ghost"
        size="icon"
        aria-label={hasCart ? cartAriaLabel : 'Cart'}
        className={cn('relative size-11 text-on-primary', activeBg)}
      >
        <Link to={cartHref}>
          <ShoppingCart aria-hidden="true" />
          {hasCart ? (
            <Badge
              data-testid="navbar-cart-count"
              aria-hidden="true"
              className="absolute -top-1 -right-1 h-4 min-w-4 rounded-full px-1 text-[10px] leading-none"
            >
              {cartLabel}
            </Badge>
          ) : null}
        </Link>
      </Button>
    </div>
  );
}

/**
 * Top-level nav item renderer — either a NavLink (plain item) or a
 * DropdownMenu trigger (group item with children).
 */
function DesktopNavItem({
  item,
  ringOffset,
}: {
  item: NavBarItem;
  ringOffset: string;
}) {
  const location = useLocation();

  if (!isNavGroup(item)) {
    return (
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          cn(
            'text-ui-nav border-b-2 border-transparent py-1 opacity-85 transition-[opacity,border-color] hover:opacity-100',
            isActive && 'border-brand-primary opacity-100',
          )
        }
      >
        {item.label}
      </NavLink>
    );
  }

  // Group: dropdown menu. Parent is "active" when any child path is a prefix
  // of the current location — keeps the underline visible while browsing
  // into any category.
  const groupActive =
    (item.to && location.pathname.startsWith(item.to)) ||
    item.children.some((child) => location.pathname.startsWith(child.to));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'text-ui-nav inline-flex items-center gap-1 border-b-2 border-transparent py-1 opacity-85 transition-[opacity,border-color] outline-none hover:opacity-100 focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
            ringOffset,
            groupActive && 'border-brand-primary opacity-100',
          )}
        >
          {item.label}
          <ChevronDown aria-hidden="true" className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {item.to ? (
          <>
            <DropdownMenuItem asChild>
              <Link to={item.to}>All {item.label}</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {item.children.map((child) => (
          <DropdownMenuItem key={`${child.label}-${child.to}`} asChild>
            <Link to={child.to}>{child.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Hook: track whether the window has scrolled past the top.
 *
 * Runs a `scroll` listener on `window` (passive), reads `window.scrollY`
 * synchronously. Returns `false` during SSR / in environments without a
 * window so the header renders its solid state by default.
 */
function useScrolledPastTop(): boolean {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handle = () => setScrolled(window.scrollY > 0);
    handle();
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);

  return scrolled;
}

export function NavBar({
  navItems,
  cartCount = 0,
  cartHref = '/cart',
  onSearchClick,
  onSignIn,
  onSignOut,
  onAccountClick,
  user,
  brand = 'DFQ',
  brandHref = '/',
  logoSrc,
  logoAlt,
  surface,
  density,
  className,
  isScrolled,
  notificationsSlot,
  primaryNavSlot,
  searchBarSlot,
  showSearch = true,
  ...rest
}: NavBarProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const detectedScrolled = useScrolledPastTop();
  const scrolled = isScrolled ?? detectedScrolled;
  const resolvedSurface: NavBarSurface = surface ?? 'default';
  const surfaceClasses = SURFACE_CLASSES[resolvedSurface];

  const handleMobileNavClick = React.useCallback(() => {
    setMobileOpen(false);
  }, []);

  return (
    <header
      className={cn(
        navBarVariants({ surface, density }),
        scrolled && surfaceClasses.scrolledBg,
        className,
      )}
      data-node-id="10:2"
      data-scrolled={scrolled ? 'true' : 'false'}
      {...rest}
    >
      <div className={NAV_BAR_INNER_CLASS}>
      {/* Brand — always visible */}
      <Link
        to={brandHref}
        className={cn(
          'text-on-primary shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
          !logoSrc && 'text-2xl font-extrabold tracking-[2px]',
          surfaceClasses.ringOffset,
        )}
        aria-label="DFQ home"
      >
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={logoAlt ?? (typeof brand === 'string' ? brand : 'DFQ Printing')}
            className="h-8 w-auto md:h-9"
          />
        ) : (
          brand
        )}
      </Link>

      {/* Desktop primary nav */}
      <nav
        aria-label="Primary"
        className="hidden md:flex md:items-center md:gap-8"
      >
        {primaryNavSlot !== undefined ? (
          <div data-slot="navbar-primary-nav">{primaryNavSlot}</div>
        ) : null}
        {navItems.map((item) => (
          <DesktopNavItem
            key={`${item.label}-${'to' in item ? item.to ?? '' : ''}`}
            item={item}
            ringOffset={surfaceClasses.ringOffset}
          />
        ))}
      </nav>

      {/* Desktop right cluster */}
      <div className="hidden md:flex">
        <RightCluster
          user={user}
          cartCount={cartCount}
          cartHref={cartHref}
          onSearchClick={onSearchClick}
          onSignIn={onSignIn}
          onSignOut={onSignOut}
          onAccountClick={onAccountClick}
          notificationsSlot={notificationsSlot}
          searchBarSlot={searchBarSlot}
          showSearch={showSearch}
          activeBg={surfaceClasses.activeBg}
          surface={resolvedSurface}
        />
      </div>

      {/* Mobile hamburger — opens a Sheet drawer */}
      <div className="flex md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Open navigation menu"
              aria-expanded={mobileOpen}
              className={cn('size-11 text-on-primary', surfaceClasses.activeBg)}
            >
              <Menu aria-hidden="true" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[280px] sm:max-w-[280px]"
            data-testid="navbar-mobile-sheet"
          >
            <SheetHeader>
              <SheetTitle>Menu</SheetTitle>
              <SheetDescription className="sr-only">
                Primary site navigation
              </SheetDescription>
            </SheetHeader>

            <nav
              aria-label="Mobile primary"
              className="flex flex-1 flex-col gap-1 px-2"
            >
              {navItems.map((item) =>
                isNavGroup(item) ? (
                  <React.Fragment key={`group-${item.label}`}>
                    <p className="text-ui-nav text-text-secondary px-3 pt-3 pb-1 text-xs tracking-wide uppercase">
                      {item.label}
                    </p>
                    {item.to ? (
                      <NavLink
                        to={item.to}
                        onClick={handleMobileNavClick}
                        className={({ isActive }) =>
                          cn(
                            'text-ui-nav text-text-primary min-h-11 rounded-md px-3 py-2 transition-colors hover:bg-muted-surface',
                            isActive && 'bg-muted-surface font-medium',
                          )
                        }
                      >
                        All {item.label}
                      </NavLink>
                    ) : null}
                    {item.children.map((child) => (
                      <NavLink
                        key={`${child.label}-${child.to}`}
                        to={child.to}
                        onClick={handleMobileNavClick}
                        className={({ isActive }) =>
                          cn(
                            'text-ui-nav text-text-primary min-h-11 rounded-md px-3 py-2 transition-colors hover:bg-muted-surface',
                            isActive && 'bg-muted-surface font-medium',
                          )
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </React.Fragment>
                ) : (
                  <NavLink
                    key={`${item.label}-${item.to}`}
                    to={item.to}
                    onClick={handleMobileNavClick}
                    className={({ isActive }) =>
                      cn(
                        'text-ui-nav text-text-primary min-h-11 rounded-md px-3 py-2 transition-colors hover:bg-muted-surface',
                        isActive && 'bg-muted-surface text-text-primary font-medium',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ),
              )}
            </nav>

            <div className="mt-auto flex flex-col gap-2 border-t border-divider p-4">
              <Button
                type="button"
                variant="ghost"
                className="min-h-11 justify-start gap-2"
                onClick={() => {
                  setMobileOpen(false);
                  onSearchClick?.();
                }}
              >
                <Search aria-hidden="true" />
                Search
              </Button>

              {user ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    className="min-h-11 justify-start gap-2"
                    onClick={() => {
                      setMobileOpen(false);
                      onAccountClick?.();
                    }}
                  >
                    <User aria-hidden="true" />
                    Account
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="min-h-11 justify-start gap-2"
                    onClick={() => {
                      setMobileOpen(false);
                      onSignOut?.();
                    }}
                  >
                    Sign out
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-11 justify-start gap-2"
                  onClick={() => {
                    setMobileOpen(false);
                    onSignIn?.();
                  }}
                >
                  <User aria-hidden="true" />
                  Sign in
                </Button>
              )}

              <Button
                asChild
                variant="ghost"
                className="relative min-h-11 justify-start gap-2"
              >
                <Link to={cartHref} onClick={handleMobileNavClick}>
                  <ShoppingCart aria-hidden="true" />
                  Cart
                  {cartCount > 0 ? (
                    <Badge
                      data-testid="navbar-mobile-cart-count"
                      className="ml-auto h-4 min-w-4 rounded-full px-1 text-[10px] leading-none"
                    >
                      {cartCount > 99 ? '99+' : String(cartCount)}
                    </Badge>
                  ) : null}
                </Link>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      </div>
    </header>
  );
}
